import React, { useEffect, useMemo, useState } from 'react';
import InputMask from 'react-input-mask';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatEventDateTime } from '../utils/dateTime';


type EventDateTimePickerProps = {
  value: Date | null; // valor confirmado (mostrado en el botón)
  draftValue: Date | null; // valor temporal dentro del popover
  onDraftChange: (value: Date | null) => void;
  onSave: () => void;
  placeholder?: string;
  buttonClassName?: string;
  align?: 'start' | 'center' | 'end';
};

const EventDateTimePicker: React.FC<EventDateTimePickerProps> = ({
  value,
  draftValue,
  onDraftChange,
  onSave,
  placeholder = 'Seleccionar fecha y hora',
  buttonClassName,
  align = 'center',
}) => {
  const todayStart = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, []);

  // Estado para el valor de hora/minutos (calculado a partir de draftValue)
  const [timeValue, setTimeValue] = useState<{ hours: string; minutes: string }>({
    hours: '00',
    minutes: '00',
  });

  function updateTime(hours?: string, minutes?: string, isDeferred = false) {
    const validH =
      hours && /^\d+$/.test(hours) && Number(hours) >= 0 && Number(hours) < 24
        ? Number(hours)
        : Number(timeValue.hours);

    const validM =
      minutes && /^\d+$/.test(minutes) && Number(minutes) >= 0 && Number(minutes) < 60
        ? Number(minutes)
        : Number(timeValue.minutes);

    const h = validH.toString().padStart(2, '0');
    const m = validM.toString().padStart(2, '0');

    if (!isDeferred) setTimeValue({ hours: h, minutes: m });
    else
      setTimeout(() => {
        setTimeValue({ hours: h, minutes: m });
      }, 0);

    if (!draftValue) return;
    const datePart = new Date(draftValue.getFullYear(), draftValue.getMonth(), draftValue.getDate());
    const normalized = new Date(
      datePart.getFullYear(),
      datePart.getMonth(),
      datePart.getDate(),
      Number(h),
      Number(m),
      0,
      0
    );
    onDraftChange(normalized);
  }

  // Mantiene timeValue sincronizado con draftValue
  useEffect(() => {
    if (!draftValue) {
      setTimeValue({ hours: '00', minutes: '00' });
    } else {
      const hours = draftValue.getHours().toString().padStart(2, '0');
      const minutes = draftValue.getMinutes().toString().padStart(2, '0');
      setTimeValue({ hours, minutes });
    }
  }, [draftValue]);

  // Función auxiliar para ajustar horas o minutos
  function adjustTime(field: 'hours' | 'minutes', delta: number) {
    let hours = Number(timeValue.hours);
    let minutes = Number(timeValue.minutes);
    if (field === 'hours') {
      hours = (hours + delta + 24) % 24;
    } else if (field === 'minutes') {
      minutes = (minutes + delta + 60) % 60;
    }
    const newH = hours.toString().padStart(2, '0');
    const newM = minutes.toString().padStart(2, '0');
    setTimeValue({ hours: newH, minutes: newM });
    if (!draftValue) return;
    const datePart = new Date(draftValue.getFullYear(), draftValue.getMonth(), draftValue.getDate());
    const normalized = new Date(
      datePart.getFullYear(),
      datePart.getMonth(),
      datePart.getDate(),
      hours,
      minutes,
      0,
      0
    );
    onDraftChange(normalized);
  }

  // Cuando se selecciona una fecha, llama a onDraftChange con la nueva fecha + hora actual
  const handleDateSelect = (next?: Date) => {
    if (!next) {
      onDraftChange(null);
      return;
    }
    const hours = Number(timeValue.hours) || 0;
    const minutes = Number(timeValue.minutes) || 0;
    const normalized = new Date(
      next.getFullYear(),
      next.getMonth(),
      next.getDate(),
      hours,
      minutes,
      0,
      0
    );
    onDraftChange(normalized);
  };

  // displayValue muestra el valor confirmado (externo, value)
  const displayValue = value ? formatEventDateTime(value) : placeholder;

  // Estado local para controlar si el popover está abierto
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-12 w-full justify-between rounded-2xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-white',
            !value && 'text-slate-400',
            buttonClassName
          )}
        >
          {displayValue}
          <CalendarIcon className="h-4 w-4 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] sm:w-[480px] p-3 bg-purple-50" sideOffset={8} align={align}>
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
          <Calendar
            mode="single"
            selected={draftValue ?? undefined}
            onSelect={(date) => handleDateSelect(date ?? undefined)}
            initialFocus
            disabled={{ before: todayStart }}
          />

          <div className="w-full space-y-3 sm:mt-0 mt-4 flex flex-col justify-center items-center">
            <Clock strokeWidth={1.2} className="h-16 w-16 text-purple-400 mb-3" />
            <div className="relative w-fit flex items-center gap-2">
              {/* Stepper horas */}
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => adjustTime('hours', 1)}
                  className="p-1 text-purple-500 hover:text-purple-700"
                  aria-label="Incrementar hora"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => adjustTime('hours', -1)}
                  className="p-1 text-purple-500 hover:text-purple-700"
                  aria-label="Reducir hora"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>

              <InputMask
                mask="99:99"
                maskChar=""
                value={`${timeValue.hours}:${timeValue.minutes}`}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value;

                  // Solo procesar si tiene el formato completo "hh:mm"
                  if (!/^\d{2}:\d{2}$/.test(value)) return;

                  const [hoursRaw, minutesRaw] = value.split(':');
                  const hours = Math.min(23, Number(hoursRaw));
                  const minutes = Math.min(59, Number(minutesRaw));

                  const h = hours.toString().padStart(2, '0');
                  const m = minutes.toString().padStart(2, '0');

                  setTimeValue({ hours: h, minutes: m });

                  if (!draftValue) return;
                  const datePart = new Date(
                    draftValue.getFullYear(),
                    draftValue.getMonth(),
                    draftValue.getDate()
                  );
                  const normalized = new Date(
                    datePart.getFullYear(),
                    datePart.getMonth(),
                    datePart.getDate(),
                    Number(h),
                    Number(m),
                    0,
                    0
                  );
                  onDraftChange(normalized);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                  e.preventDefault();
                  const cursorPos = (e.target as HTMLInputElement).selectionStart || 0;
                  const isHour = cursorPos <= 2;
                  const delta = e.key === 'ArrowUp' ? 1 : -1;
                  let hours = Number(timeValue.hours);
                  let minutes = Number(timeValue.minutes);
                  if (isHour) {
                    hours = (hours + delta + 24) % 24;
                  } else {
                    minutes = (minutes + delta + 60) % 60;
                  }
                  const newH = hours.toString().padStart(2, '0');
                  const newM = minutes.toString().padStart(2, '0');
                  setTimeValue({ hours: newH, minutes: newM });
                  if (!draftValue) return;
                  const datePart = new Date(draftValue.getFullYear(), draftValue.getMonth(), draftValue.getDate());
                  const normalized = new Date(
                    datePart.getFullYear(),
                    datePart.getMonth(),
                    datePart.getDate(),
                    hours,
                    minutes,
                    0,
                    0
                  );
                  onDraftChange(normalized);
                }}
                placeholder="00:00"
                className="h-10 w-24 rounded-md border border-slate-200 bg-white px-2 text-lg font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400 text-center"
              />

              {/* Stepper minutos */}
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => adjustTime('minutes', 1)}
                  className="p-1 text-purple-500 hover:text-purple-700"
                  aria-label="Incrementar minutos"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => adjustTime('minutes', -1)}
                  className="p-1 text-purple-500 hover:text-purple-700"
                  aria-label="Reducir minutos"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            </div>
            {/* Guardar button */}
            <Button
              onClick={() => {
                onSave();
                setOpen(false);
              }}
              disabled={!draftValue || (draftValue?.getTime() === value?.getTime())}
              className="mt-4 bg-purple-500 hover:bg-purple-600 text-white rounded-xl px-4 py-2"
            >
              Guardar
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EventDateTimePicker;
